import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { versionService } from '@/services';

interface VersionContextType {
  serverVersion: string;
  isLoading: boolean;
  refreshVersion: () => Promise<void>;
}

const VersionContext = createContext<VersionContextType | undefined>(undefined);

// 半小时间隔 (30 * 60 * 1000 ms)
const POLL_INTERVAL = 1 * 60 * 1000;

// 轮询时间段配置
const POLL_SCHEDULE = {
  // 测试模式：全天每分钟轮询
  // TODO: 测试完成后改为 false
  isTestMode: false,
  // 晚上 18:00 - 22:00 每半小时轮询
  evening: { start: 18, end: 22, interval: POLL_INTERVAL },
  // 早上 8:00 轮询一次
  morning: { hour: 8 },
};

// 检查当前是否在轮询时间段内
const shouldPollNow = (): boolean => {
  // 测试模式：始终返回 true
  if (POLL_SCHEDULE.isTestMode) {
    return true;
  }

  const now = new Date();
  const hour = now.getHours();
  const minute = now.getMinutes();

  // 晚上 18:00 - 22:00
  if (hour >= POLL_SCHEDULE.evening.start && hour < POLL_SCHEDULE.evening.end) {
    return true;
  }

  // 早上 8:00 整点
  if (hour === POLL_SCHEDULE.morning.hour && minute === 0) {
    return true;
  }

  return false;
};

// 获取下次轮询的延迟时间（毫秒）
const getNextPollDelay = (): number => {
  // 测试模式：每分钟轮询一次
  if (POLL_SCHEDULE.isTestMode) {
    return POLL_INTERVAL;
  }

  const now = new Date();
  const currentHour = now.getHours();
  const currentMinute = now.getMinutes();
  const currentSecond = now.getSeconds();

  // 如果在晚上 18:00-22:00 时段内，返回到下一个半小时的延迟
  if (currentHour >= POLL_SCHEDULE.evening.start && currentHour < POLL_SCHEDULE.evening.end) {
    const nextHalfHour = currentMinute < 30 ? 30 : 60;
    const targetMinute = nextHalfHour === 60 ? 0 : nextHalfHour;
    const targetHour = nextHalfHour === 60 ? currentHour + 1 : currentHour;
    
    if (targetHour >= POLL_SCHEDULE.evening.end) {
      // 超出晚间时段，计算到早上 8:00 的延迟
      return getDelayToMorning8();
    }
    
    const targetTime = new Date(now);
    targetTime.setHours(targetHour, targetMinute, 0, 0);
    return targetTime.getTime() - now.getTime();
  }

  // 如果在晚间时段之前，计算到 18:00 的延迟
  if (currentHour < POLL_SCHEDULE.evening.start) {
    const targetTime = new Date(now);
    targetTime.setHours(POLL_SCHEDULE.evening.start, 0, 0, 0);
    return targetTime.getTime() - now.getTime();
  }

  // 如果在 22:00 之后且早于早上 8:00
  if (currentHour >= POLL_SCHEDULE.evening.end || currentHour < POLL_SCHEDULE.morning.hour) {
    return getDelayToMorning8();
  }

  return POLL_INTERVAL;
};

// 计算到早上 8:00 的延迟
const getDelayToMorning8 = (): number => {
  const now = new Date();
  const targetTime = new Date(now);
  
  if (now.getHours() < POLL_SCHEDULE.morning.hour) {
    // 当天早上 8:00
    targetTime.setHours(POLL_SCHEDULE.morning.hour, 0, 0, 0);
  } else {
    // 次天早上 8:00
    targetTime.setDate(targetTime.getDate() + 1);
    targetTime.setHours(POLL_SCHEDULE.morning.hour, 0, 0, 0);
  }
  
  return targetTime.getTime() - now.getTime();
};

interface VersionProviderProps {
  children: ReactNode;
}

export const VersionProvider: React.FC<VersionProviderProps> = ({ children }) => {
  const [serverVersion, setServerVersion] = useState<string>('unknown');
  const [isLoading, setIsLoading] = useState(true);

  const fetchVersion = useCallback(async () => {
    try {
      // 每次请求前清除缓存，确保获取最新版本
      versionService.clearCache();
      
      const version = await versionService.getVersion();
      const localVersion = versionService.getLocalVersion();
      
      console.log(`[VersionContext] 本地版本: ${localVersion}, 服务器版本: ${version}`);
      setServerVersion(version);

      // 比较版本，如果服务器版本更新则刷新页面
      if (version && version !== 'unknown' && versionService.isServerNewer(version)) {
        console.log(`[VersionContext] 检测到新版本 (${version} > ${localVersion})，正在刷新...`);
        // 延迟一下让用户看到提示
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      }
    } catch (error) {
      console.error('获取服务器版本失败:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const refreshVersion = useCallback(async () => {
    versionService.clearCache();
    await fetchVersion();
  }, [fetchVersion]);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;

    const scheduleNextPoll = () => {
      const delay = getNextPollDelay();
      console.log(`[VersionContext] 下次轮询将在 ${Math.round(delay / 1000 / 60)} 分钟后执行`);
      
      timer = setTimeout(async () => {
        if (shouldPollNow()) {
          await fetchVersion();
        }
        scheduleNextPoll();
      }, delay);
    };

    // 初始获取
    fetchVersion();
    
    // 启动定时轮询
    scheduleNextPoll();

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [fetchVersion]);

  return (
    <VersionContext.Provider value={{ serverVersion, isLoading, refreshVersion }}>
      {children}
    </VersionContext.Provider>
  );
};

export const useVersion = (): VersionContextType => {
  const context = useContext(VersionContext);
  if (!context) {
    throw new Error('useVersion 必须在 VersionProvider 内使用');
  }
  return context;
};
